import React from 'react';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import { createStyles, makeStyles, type Theme } from '@material-ui/core/styles';
import Alert from '@material-ui/lab/Alert';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import type { MoleculerListResult } from '../../../@sprinx/knihovka-types';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import { CatalogueProduct, catalogueViewModeState } from '../../../api/products/catalogue';
import CataloguePageListContentItemCard from './CataloguePageListContentItemCard';
import CataloguePageListContentItemRow from './CataloguePageListContentItemRow';

export type CataloguePageListContentProps = StandardDivProps<CataloguePageListContentClassKey> & {
  catalogue: MoleculerListResult<CatalogueProduct> | undefined;
};

export type CataloguePageListContentClassKey = 'root' | 'emptyMessageContainer';

const themeSettings = { name: 'CataloguePageListContent' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CataloguePageListContentClassKey, {}>({
      root: {
        display: 'flex',
        flexWrap: 'wrap',
      },
      emptyMessageContainer: {
        padding: theme.spacing(3, 3),
        display: 'flex',
        width: '100%',
        justifyContent: 'center',
      },
    }),
  themeSettings,
);

const CataloguePageListContent = React.forwardRef<HTMLDivElement, CataloguePageListContentProps>(
  ({ className, classes: pClasses, catalogue }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const t = useTranslateWithFallback();
    const catalogueView = useRecoilValue(catalogueViewModeState);

    const ItemComponent = {
      row: CataloguePageListContentItemRow,
      card: CataloguePageListContentItemCard,
    }[catalogueView];

    return (
      <div ref={ref} className={clsx(classes.root, className)}>
        {catalogue && catalogue.rows.length > 0 ? (
          catalogue.rows.map((product, idx) => <ItemComponent key={idx.toString()} product={product} />)
        ) : (
          <div className={classes.emptyMessageContainer}>
            <Alert variant='outlined' severity='warning'>
              {t('cataloguePage/noMatch')}
            </Alert>
          </div>
        )}
      </div>
    );
  },
);

CataloguePageListContent.displayName = 'CataloguePageListContent';

export default withThemeProps(themeSettings)(CataloguePageListContent);
